<template>
  <NotFoundComponent v-if="notFound" />
  <template v-else>
    <div class="grey-box" v-if="order && product">
      <div class="checkout-page-item">
        <img :src="product.imageUrl" class="checkout-page-item-image" />
        <div class="checkout-page-item-info">
          <p class="body-text-bold">
            {{ product.name }}
          </p>
          <p class="body-text-bold">
            {{ store.formatPrice(product.price) }}
          </p>
          <p class="body-text-bold">{{ __('item_quantity') }}: {{ order.amount }}</p>

          <p class="body-text-bold primary-text">
            {{ store.formatPrice(order.price * order.amount) }}
          </p>
        </div>
      </div>
    </div>
    <div class="checkout-page">
      <form id="payment-form">
        <p class="title-text">{{ __('checkout_card_title') }}</p>
        <div id="stripe-payment"></div>
        <div id="payment-message" class="hidden normal-text-bold error-text"></div>

        <button id="submit" class="button">
          <img src="@/assets/icons/starIcon.svg" />
          {{ __('payment_confirm_button') }}
        </button>
      </form>
    </div>
  </template>
</template>
<script lang="ts" setup>
import router from '@/router';
import { loadStripe } from '@stripe/stripe-js';
import axios from 'axios';
import { onMounted, ref, watch } from 'vue';
import type { Stripe, StripeElementsOptionsClientSecret, StripeElements, StripeElementLocale } from '@stripe/stripe-js';
import { Order, Product } from '@/types';
import { generalStore } from '@/store';
import NotFoundComponent from '@/components/NotFoundComponent.vue';
import __, { getLanguage, language } from '@/translate';

const stripeKey = ref('');

let stripe: Stripe | null = null;
let elements: StripeElements;

const notFound = ref(false);
const order = ref<Order | null>(null);
const product = ref<Product | null>(null);
const store = generalStore();
const stripeElementOptions: StripeElementsOptionsClientSecret = {
  locale: getLanguage() as StripeElementLocale,
  fonts: [
    {
      family: 'Montserrat',
      src: 'url(https://nordic-dental.si/fonts/Montserrat-Regular.ttf)',
      weight: '400',
    },
    {
      family: 'Montserrat',
      src: 'url(https://nordic-dental.si/fonts/Montserrat-Medium.ttf)',
      weight: '500',
    },
    {
      family: 'Montserrat',
      src: 'url(https://nordic-dental.si/fonts/Montserrat-SemiBold.ttf)',
      weight: '600',
    },
    {
      family: 'Montserrat',
      src: 'url(https://nordic-dental.si/fonts/Montserrat-Bold.ttf)',
      weight: '700',
    },
  ],
  appearance: {
    theme: 'stripe',
    variables: {
      fontFamily: 'Montserrat',
      fontSmooth: 'auto',
      fontSizeBase: '16px',
      fontSizeSm: '12px',
      fontWeightLight: '500',
      fontWeightNormal: '500',
      fontWeightBold: '700',
      borderRadius: '0px',
      gridRowSpacing: '20px',
      gridColumnSpacing: '20px',
      tabSpacing: '4px',
      colorPrimary: '#15bbbd',
      // colorBackground: '';
      colorText: '#6d6e71',
      colorSuccess: '#15bbbd',
      colorDanger: '#ff0000',
      // colorWarning?: string;
      colorTextSecondary: '#8c8c8c',
      colorTextPlaceholder: '#b6b8bd',
      iconColor: '#15bbbd',
      iconHoverColor: '#15bbbdb2',
      iconCardErrorColor: '#ff0000',
      iconCardCvcColor: '#15bbbdb2',
      iconCardCvcErrorColor: '#ff0000',
      tabIconColor: '#15bbbd',
      tabIconHoverColor: '#15bbbdb2',
      tabIconSelectedColor: '#15bbbdb2',
      tabIconMoreColor: '#15bbbd',
      tabIconMoreHoverColor: '#15bbbdb2',
      focusBoxShadow: 'none',
      focusOutline: 'none',
    },
    // labels: 'floating',
    rules: {
      '.Label': {
        textTransform: 'uppercase',
        marginBottom: '10px',
      },
      '.Input:hover': {
        borderColor: '#15bbbdb2',
      },
      '.Input': {
        boxShadow: 'none',
      },
    },
  },
  clientSecret: '',
};

watch(language, (newLanguage) => {
  stripeElementOptions.locale = newLanguage as StripeElementLocale;
  if (elements) {
    elements.update({ locale: newLanguage as StripeElementLocale });
  }
});

onMounted(async () => {
  if (!router.currentRoute.value.params.uuid) {
    notFound.value = true;
    return;
  }

  await Promise.all([fetchStripeKey(), fetchStripeClientSecret(), fetchOrderDetails()]);

  if (!order.value) {
    notFound.value = true;
    return;
  }

  await fetchProductDetails();

  stripe = await loadStripe(stripeKey.value);

  if (!stripe) {
    notFound.value = true;
    return;
  }

  elements = stripe.elements(stripeElementOptions);

  const paymentElement = elements.create('payment', {
    fields: {
      billingDetails: 'auto',
    },
    defaultValues: {
      billingDetails: {
        address: {
          country: 'SI',
        },
      },
    },
    business: {
      name: 'Nordic Dental',
    },
    terms: {
      applePay: 'always',
      auBecsDebit: 'always',
      bancontact: 'always',
      card: 'always',
      cashapp: 'always',
      googlePay: 'always',
      ideal: 'always',
      paypal: 'always',
      sepaDebit: 'always',
      sofort: 'always',
      usBankAccount: 'always',
    },
    wallets: {
      googlePay: 'auto',
      applePay: 'auto',
    },
    layout: {
      type: 'tabs',
      defaultCollapsed: false,
    },
  });

  paymentElement.mount('#stripe-payment');
  document.querySelector('#payment-form')?.addEventListener('submit', handleSubmit);
});

function fetchStripeKey() {
  return axios.get('/api/stripe/config').then((response) => {
    stripeKey.value = response.data.publishableKey;
  });
}

function fetchStripeClientSecret() {
  return axios.post(`/api/orders/${router.currentRoute.value.params.uuid}/intents`).then((response) => {
    stripeElementOptions.clientSecret = response.data.clientSecret;
  });
}

function fetchOrderDetails() {
  return axios
    .get(`/api/orders/${router.currentRoute.value.params.uuid}`)
    .then((response) => {
      order.value = response.data;
    })
    .catch(() => {
      notFound.value = true;
    });
}

function fetchProductDetails() {
  return axios.get(`/api/products/${order.value?.productId}`).then((response) => {
    product.value = response.data;
  });
}

async function handleSubmit(e: Event) {
  e.preventDefault();

  if (!stripe) {
    return;
  }

  setLoading(true);

  const { error } = await stripe.confirmPayment({
    elements,
    confirmParams: {
      // Make sure to change this to your payment completion page
      return_url: window.publicURL + '/payment/success/' + order.value?.uuid,
    },
  });

  // This point will only be reached if there is an immediate error when
  // confirming the payment. Otherwise, your customer will be redirected to
  // your `return_url`. For some payment methods like iDEAL, your customer will
  // be redirected to an intermediate site first to authorize the payment, then
  // redirected to the `return_url`.
  if ((error.type === 'card_error' || error.type === 'validation_error') && error.message) {
    showMessage(error.message);
  } else {
    showMessage('An unexpected error occurred.');
  }

  setLoading(false);
}

function showMessage(messageText: string) {
  const messageContainer = document.querySelector('#payment-message');

  if (!messageContainer) {
    return;
  }

  messageContainer.classList.remove('hidden');
  messageContainer.textContent = messageText;

  setTimeout(function () {
    messageContainer.classList.add('hidden');
    messageContainer.textContent = '';
  }, 4000);
}

function setLoading(isLoading: boolean) {
  const submit = document.querySelector('#submit') as HTMLButtonElement;
  if (isLoading) {
    // Disable the button and show a spinner
    if (submit) {
      submit.disabled = true;
    }
    document.querySelector('#spinner')?.classList.remove('hidden');
    document.querySelector('#button-text')?.classList.add('hidden');
  } else {
    if (submit) {
      submit.disabled = false;
    }
    document.querySelector('#spinner')?.classList.add('hidden');
    document.querySelector('#button-text')?.classList.remove('hidden');
  }
}
</script>
